// src/App.jsx
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import RequireAuth from "./auth/RequireAuth";
import PublicOnly from "./auth/PublicOnly";
import { useAuth } from "./auth/AuthProvider";

import Login from "./pages/login";
import ForgotPasswordPage from "./pages/forgot-password";
import ResetPasswordPage from "./pages/reset-password";

import AdminDashboard from "./pages/admin-dashboard";
import AppointmentCalendar from "./pages/appointment-calendar";
import ServiceConfiguration from "./pages/service-configuration";
import StaffManagement from "./pages/staff-management";
import ClientManagement from "./pages/client-management";

import InventoryPage from "./pages/inventory";

import SmsPage from "./pages/sms";
import SmsCampaignsPage from "./pages/sms-campaigns";

import BusinessSettingsPage from "./pages/business/BusinessSettingsPage";
import LocationChangePage from "./pages/business/LocationChangePage";

import PublicChatPage from "./pages/public-chat";
import PublicBookingPage from "./pages/public/PublicBookingPage";
import AppointmentChatPage from "@/pages/appointment-chat";

import SubscriptionSuspendedPage from "./pages/subscription-suspended";
import BillingPage from "./pages/billing";
import ConnectOnboardingPage from "./pages/billing/ConnectOnboarding";
import PaymentSettingsPage from "./pages/settings/PaymentSettings";

import RequireTenant from "./auth/RequireTenant";
import BusinessNotFoundPage from "./pages/business-not-found";
import DemoPlanSelectPage from "./pages/demo-plan-select";

function LegacyLocationRedirect() {
  const { id } = useParams();
  return <Navigate to={`/app/business/locations/${id}`} replace />;
}

function AppHomeRedirect() {
  const { permissions } = useAuth();
  const canSeeDashboard = Boolean(permissions?.canSeeDashboard);

  return canSeeDashboard ? (
    <Navigate to="/app/admin-dashboard" replace />
  ) : (
    <Navigate to="/app/appointment-calendar" replace />
  );
}

/**
 * Routing strategy
 * - Internal app lives under /app: https://{slug}.appointiq.ro/app
 * - Public chat lives at /chat/:token
 */

export default function App() {
  const isDemoMode = import.meta.env.VITE_DEMO_MODE === "true";

  return (
    <BrowserRouter>
      <Routes>
        {/* =====================
            PUBLIC
          ===================== */}
        <Route
          path="/"
          element={
            isDemoMode ? (
              <DemoPlanSelectPage />
            ) : (
              <RequireTenant>
                <Navigate to="/app/login" replace />
              </RequireTenant>
            )
          }
        />


        {/* Public chat (client) */}
        <Route path="/chat/:token" element={<PublicChatPage />} />

        {/* Business not found */}
        <Route
          path="/business-not-found"
          element={
            isDemoMode ? <Navigate to="/" replace /> : <BusinessNotFoundPage />
          }
        />

        {/* Public booking */}
        <Route path="/book" element={isDemoMode ? <Navigate to="/app" replace /> : <PublicBookingPage />} />
        <Route path="/p/:businessSlug" element={isDemoMode ? <Navigate to="/app" replace /> : <PublicBookingPage />} />
        <Route path="/billing" element={<Navigate to="/app/billing" replace />} />

        {/* =====================
            INTERNAL APP (auth)
           ===================== */}
        <Route
          path="/app/login"
          element={
            isDemoMode ? (
              <Navigate to="/" replace />
            ) : (
              <RequireTenant>
                <PublicOnly>
                  <Login />
                </PublicOnly>
              </RequireTenant>
            )
          }
        />

        <Route
          path="/app/forgot-password"
          element={
            isDemoMode ? (
              <Navigate to="/" replace />
            ) : (
              <RequireTenant>
                <PublicOnly>
                  <ForgotPasswordPage />
                </PublicOnly>
              </RequireTenant>
            )
          }
        />

        <Route
          path="/app/reset-password"
          element={
            isDemoMode ? (
              <Navigate to="/" replace />
            ) : (
              <RequireTenant>
                <PublicOnly>
                  <ResetPasswordPage />
                </PublicOnly>
              </RequireTenant>
            )
          }
        />

        {/* =====================
            INTERNAL APP (protected)
           ===================== */}
        <Route
          path="/app"
          element={
            <RequireTenant>
              <RequireAuth>
                <AppHomeRedirect />
              </RequireAuth>
            </RequireTenant>
          }
        />

        <Route
          path="/app/subscription-suspended"
          element={
            <RequireTenant>
              <RequireAuth>
                <SubscriptionSuspendedPage />
              </RequireAuth>
            </RequireTenant>
          }
        />

        <Route
          path="/app/billing"
          element={
            <RequireTenant>
              <RequireAuth requiredRoles={["owner", "director"]}>
                <BillingPage />
              </RequireAuth>
            </RequireTenant>
          }
        />

        <Route
          path="/app/settings/payments"
          element={
            <RequireTenant>
              <RequireAuth requiredRoles={["owner"]}>
                <PaymentSettingsPage />
              </RequireAuth>
            </RequireTenant>
          }
        />

        <Route
          path="/app/settings/payments/connect"
          element={
            <RequireTenant>
              <RequireAuth requiredRoles={["owner"]}>
                <ConnectOnboardingPage />
              </RequireAuth>
            </RequireTenant>
          }
        />

        <Route
          path="/app/admin-dashboard"
          element={
            <RequireTenant>
              <RequireAuth requiredRoles={["owner", "director"]} requiredFeature="stats">
                <AdminDashboard />
              </RequireAuth>
            </RequireTenant>
          }
        />

        <Route
          path="/app/appointment-calendar"
          element={
            <RequireTenant>
              <RequireAuth>
                <AppointmentCalendar />
              </RequireAuth>
            </RequireTenant>
          }
        />

        <Route
          path="/app/service-configuration"
          element={
            <RequireTenant>
              <RequireAuth requiredRoles={["owner", "director"]} requiredFeature="services">
                <ServiceConfiguration />
              </RequireAuth>
            </RequireTenant>
          }
        />

        <Route
          path="/app/inventory"
          element={
            <RequireTenant>
              <RequireAuth
                requiredRoles={["owner", "director", "reception"]}
                requiredFeature="inventory"
              >
                <InventoryPage />
              </RequireAuth>
            </RequireTenant>
          }
        />


        <Route
          path="/app/staff-management"
          element={
            <RequireTenant>
              <RequireAuth requiredRoles={["owner", "director"]} requiredFeature="staff">
                <StaffManagement />
              </RequireAuth>
            </RequireTenant>
          }
        />

        <Route
          path="/app/client-management"
          element={
            <RequireTenant>
              <RequireAuth
                requiredRoles={["owner", "director", "reception"]}
                requiredFeature="clients"
              >
                <ClientManagement />
              </RequireAuth>
            </RequireTenant>
          }
        />

        <Route
          path="/app/sms"
          element={
            <RequireTenant>
              <RequireAuth requiredRoles={["owner", "director", "reception"]} requiredFeature="sms">
                <SmsPage />
              </RequireAuth>
            </RequireTenant>
          }
        />

        <Route
          path="/app/sms-campaigns"
          element={
            <RequireTenant>
              <RequireAuth requiredRoles={["owner", "director", "reception"]} requiredFeature="sms">
                <SmsCampaignsPage />
              </RequireAuth>
            </RequireTenant>
          }
        />

        <Route
          path="/app/business/settings"
          element={
            <RequireTenant>
              <RequireAuth>
                <BusinessSettingsPage />
              </RequireAuth>
            </RequireTenant>
          }
        />

        <Route
          path="/app/business/locations/:id"
          element={
            <RequireTenant>
              <RequireAuth>
                <LocationChangePage />
              </RequireAuth>
            </RequireTenant>
          }
        />

        {/* Staff chat (internal) */}
        <Route
          path="/app/appointments/:appointmentId/chat"
          element={
            <RequireTenant>
              <RequireAuth>
                <AppointmentChatPage />
              </RequireAuth>
            </RequireTenant>
          }
        />

        {/* =====================
            Legacy redirects (old internal routes)
           ===================== */}
        <Route path="/login" element={<Navigate to={isDemoMode ? "/" : "/app/login"} replace />} />
        <Route path="/forgot-password" element={<Navigate to={isDemoMode ? "/" : "/app/forgot-password"} replace />} />
        <Route path="/reset-password" element={<Navigate to={isDemoMode ? "/" : "/app/reset-password"} replace />} />

        <Route path="/admin-dashboard" element={<Navigate to="/app/admin-dashboard" replace />} />
        <Route
          path="/appointment-calendar"
          element={<Navigate to="/app/appointment-calendar" replace />}
        />
        <Route
          path="/service-configuration"
          element={<Navigate to="/app/service-configuration" replace />}
        />
        <Route path="/staff-management" element={<Navigate to="/app/staff-management" replace />} />
        <Route
          path="/client-management"
          element={<Navigate to="/app/client-management" replace />}
        />
        <Route path="/sms" element={<Navigate to="/app/sms" replace />} />
        <Route path="/sms-campaigns" element={<Navigate to="/app/sms-campaigns" replace />} />
        <Route path="/business/settings" element={<Navigate to="/app/business/settings" replace />} />
        <Route path="/business/locations/:id" element={<LegacyLocationRedirect />} />

        {/* Orice altceva → root */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
